import { useAddMoreImagesContext } from '../AddMoreImagesContext'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { useQueryClient } from 'react-query'
import { reportError } from 'utils/error'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

type Props = {
  onNext: () => void
  brandKitId: number
  onSuccess?: (newMediaFiles: IMediaFile[]) => void
}

export const useAddBrandKitImages = ({ onNext, brandKitId, onSuccess }: Props) => {
  const { selectedImages, setSelectedImages, setAvailableImages } = useAddMoreImagesContext()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectKey = useUploadsProject(workspaceId).data?.project?.id
  const queryClient = useQueryClient()

  const addBrandKitImages = async () => {
    if (!uploadsProjectKey) {
      showToastMessage('Unable to find uploads folder', 'error')
      reportError('Unable to find uploads folder')
      return
    }

    const dismissToast = showToastMessage('Uploading...', 'loading', undefined, 0)

    const createResponses = await Promise.all(
      selectedImages.map((img) => {
        return MediaFileClient.create(workspaceId, {
          source_url: img.url,
          mime_type: img.mime_type || '',
          title: 'AI Generated Image',
          brand_kit_id: brandKitId,
          project_key: uploadsProjectKey,
        })
      }),
    )

    const createdMediaFiles: IMediaFile[] = []
    for (const response of createResponses) {
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        const { almanac_file } = response.data
        createdMediaFiles.push({ ...almanac_file, asset_id: almanac_file.media_file_id })
      } else {
        reportError('Failed to create media file', { metaData: { response } })
      }
    }

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
    queryClient.invalidateQueries({
      queryKey: ['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles', 'list'],
      exact: false,
    })

    onSuccess?.(createdMediaFiles)

    if (typeof dismissToast === 'function') {
      dismissToast()
    }
    onNext()

    setAvailableImages([])
    setSelectedImages([])
  }

  return addBrandKitImages
}
